
import './style.scss';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import { useUserContext } from '../../contexts/UserContext';
import { useLanguageContext } from '../../contexts/LanguageContext';
import Pagination from './_Pagination';
import EmptyRows from './_EmptyRows';
import Filter from './_Filter';
import { useEffect, useState, useCallback } from 'react';
import { RegistrationStatusEnum, RegistrationStatusData } from '../../data/Enums';
import DefaultConstants from '../../data/Constants';
import { GridColumns, DataGridProps } from "@mui/x-data-grid";
import StringHelper from '../../helpers/StringHelper';


interface IDataGridProps extends Partial<DataGridProps> {
    columns: GridColumns;
    data: any[];
    rowsPerPage?: number | undefined;
    checkboxSelection?: boolean;
    openSearch?: boolean;
    hasPagination?: boolean;
    handleCloseSearch?: () => void;
}


const DataGrid: React.FunctionComponent<IDataGridProps> = ({
    columns,
    data,
    rowsPerPage = undefined,
    openSearch = false,
    hasPagination = true,
    autoHeight = false,
    handleCloseSearch = () => {},
    onCellDoubleClick = () => {},
    getCellClassName = undefined,
    getRowClassName = undefined,
    checkboxSelection = false,
    disableSelectionOnClick = undefined,
    isRowSelectable = undefined,
    onSelectionModelChange = undefined
}) => {
    const { translate } = useLanguageContext();
    const { user } = useUserContext();

    const [search, setSearch] = useState<string>('');
    const [rows, setRows] = useState<any[]>(data);

    useEffect(() => {
        setRows(data);
        handleSearch(search);

        // eslint-disable-next-line
    }, [data]);

    const handleSearch = useCallback((value: string) => {
        setSearch(value);

        const searchRegex = new RegExp(StringHelper.Normalize(value), 'i');
        let filteredRows = data;

        if (value.trim().length > 0) {
            filteredRows = data.filter((row: any) => {
                return columns.some((column: any) => {
                    let rowColumn = row[column.field];
    
                    if (column.fieldKey) {
                        rowColumn = rowColumn[column.fieldKey];
                    }
                    
                    if (rowColumn) {
                        let rowValue = rowColumn.toString();
    
                        if (column.type === DefaultConstants.dataGridRegistrationStatusType) {
                            rowValue = translate(RegistrationStatusData[rowValue as RegistrationStatusEnum].name);
                        }
                        
                        return searchRegex.test(rowValue);
                    }
    
                    return false;
                });
            });
        }

        setRows(filteredRows);
        // eslint-disable-next-line
    }, [data, columns]);


    return (
        <>
            <MuiDataGrid
                columns={columns}
                rows={rows}
                pageSize={rowsPerPage ?? user?.rowsPerPage ?? 20}
                pagination={hasPagination ? true : undefined}
                hideFooter={!hasPagination}
                hideFooterPagination={!hasPagination}
                components={{
                    NoRowsOverlay: EmptyRows,
                    Pagination: hasPagination ? Pagination : undefined,
                    Toolbar: Filter,
                }}
                componentsProps={{
                    toolbar: {
                        open: openSearch,
                        onClose: () => {
                            handleSearch('');
                            handleCloseSearch();
                        },
                        value: search,
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSearch(event.target.value)
                    },
                }}
                checkboxSelection={checkboxSelection}
                disableSelectionOnClick={disableSelectionOnClick}
                disableColumnMenu={true}
                className={`datagrid-default`}
                onCellDoubleClick={onCellDoubleClick}
                autoHeight={autoHeight}
                getCellClassName={getCellClassName}
                getRowClassName={getRowClassName}
                isRowSelectable={isRowSelectable}
                onSelectionModelChange={onSelectionModelChange}
            />
        </>
    );
}

export default DataGrid;